/**
 * Small shared contracts and utilities for run-scoped executor workloads.
 *
 * Workload-specific controllers retain their own row state and policy. The
 * runner only depends on this lifecycle surface when deciding whether a model
 * may finish, needs a forced continuation, or must release run-scoped state.
 */

export interface ExecutorWorkloadCompletionIssue {
  issue: string;
  workloadId: string | null;
  nextTool: string;
  remainingRows: number | null;
  nextRowNumbers: readonly number[];
  instruction: string;
}

export interface ExecutorWorkloadParticipant<
  TIssue extends ExecutorWorkloadCompletionIssue =
    ExecutorWorkloadCompletionIssue,
> {
  readonly progressVersion: number;
  readonly isComplete: boolean;
  completionIssue(): TIssue | null;
  describeIncompleteRows(): string[];
  reset(): void;
}

/** Exact worksheet rectangle reserved by one active workload. */
export interface WorksheetWorkloadOutputScope {
  workloadId: string;
  workloadLabel: string;
  ownerStepId: string | null;
  sheetName: string;
  targetAddress: string;
}

/**
 * Backward-compatible shape used by the ID-lookup workload before worksheet
 * reservations became generic.
 */
export type IdLookupOutputScope = Omit<
  WorksheetWorkloadOutputScope,
  "workloadLabel"
>;

/**
 * Aborts as soon as `signal` aborts, letting a caller additionally abort the
 * derived signal without touching the run-scoped one it was linked to.
 */
export function createLinkedAbortController(signal?: AbortSignal): {
  controller: AbortController;
  cleanup: () => void;
} {
  const controller = new AbortController();
  if (!signal) return { controller, cleanup: () => {} };
  if (signal.aborted) {
    controller.abort();
    return { controller, cleanup: () => {} };
  }
  const onAbort = (): void => controller.abort();
  signal.addEventListener("abort", onAbort, { once: true });
  return {
    controller,
    cleanup: () => signal.removeEventListener("abort", onAbort),
  };
}

export interface ConcurrencyLimitOptions {
  /**
   * Runs once, on the first rejection. Workloads whose requests are billable
   * use it to abort work that is already in flight.
   */
  onFirstFailure?: (error: unknown) => void;
}

/**
 * Maps a finite collection with bounded concurrency while preserving input
 * order in the returned results.
 *
 * Stops handing out new items after the first rejection and rethrows that
 * rejection once every worker has settled. Workers never reject on their own,
 * so a failure can neither strand an unhandled rejection nor let siblings keep
 * dispatching non-idempotent work the caller has already given up on.
 */
export async function mapWithConcurrencyLimit<T, R>(
  items: readonly T[],
  limit: number,
  mapper: (item: T) => Promise<R>,
  options: ConcurrencyLimitOptions = {},
): Promise<R[]> {
  if (items.length === 0) return [];

  const normalizedLimit = Number.isFinite(limit)
    ? Math.max(1, Math.floor(limit))
    : 1;
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  let failure: { error: unknown } | undefined;
  const workerCount = Math.min(normalizedLimit, items.length);

  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length && !failure) {
        const index = nextIndex;
        nextIndex += 1;
        try {
          results[index] = await mapper(items[index]);
        } catch (error) {
          if (!failure) {
            failure = { error };
            options.onFirstFailure?.(error);
          }
          return;
        }
      }
    }),
  );

  if (failure) throw failure.error;
  return results;
}
